import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OrderHeader from '../components/order/OrderHeader';
import { getOrderStatus } from '../api';
import './Order.css';
import './OrderStatus.css';

// The curator's own page. There are no accounts - whoever holds the token
// in the link is treated as the curator, which is why the link itself is
// the thing to keep private.
//
// Read top to bottom it answers: where is my board, who is painting, what
// did I pay. On a split order the invite link comes first, because while
// sign-ups are open it is the only thing there is to do.

const DATE_LOCALE = { nl: 'nl-NL', en: 'en-GB' };

// Which colour a state reads as: needs you, in hand, done.
const TONE = {
  waiting_address: 'warn',
  to_make: 'busy',
  ready: 'busy',
  shipped: 'done',
  collected: 'done',
  cancelled: 'off',
};

// The backend tracks "to make" and "ready" separately because the workshop
// needs the difference. A customer doesn't - both mean we have it and they
// don't, so they collapse into one sentence. The exception is a pickup
// order, where "ready" is the moment they can come and get it.
function useStateLabel() {
  const { t } = useTranslation();
  return (status, isPickup) => {
    if (!status) return null;
    if (status === 'to_make' || status === 'ready') {
      if (!isPickup) return t('orderStatus.state.preparing');
      return status === 'ready'
        ? t('orderStatus.state.readyToCollect')
        : t('orderStatus.state.preparingPickup');
    }
    return t(`orderStatus.state.${status}`);
  };
}

function StateText({ status, isPickup }) {
  const label = useStateLabel()(status, isPickup);
  if (!status) return <span className="st-dim">&mdash;</span>;
  return <span className={`st-state st-state-${TONE[status]}`}>{label}</span>;
}

function InviteCard({ url, spotsLeft, spotsTotal, deadline, locale }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // Clipboard blocked (no https, or the browser said no). The field is
      // selectable, so there's still a way to get the link out.
    }
  }

  const closes = deadline
    ? new Date(`${deadline}T12:00:00`).toLocaleDateString(locale, { day: 'numeric', month: 'long' })
    : null;

  return (
    <section className="st-invite">
      <div className="st-invite-row">
        <input
          readOnly
          value={url}
          onFocus={(e) => e.target.select()}
          aria-label={t('orderStatus.invite.label')}
        />
        <button type="button" className="of-btn" onClick={copy}>
          {copied ? t('orderStatus.invite.copied') : t('orderStatus.invite.copy')}
        </button>
      </div>
      <p className="st-invite-foot">
        {t('orderStatus.invite.spots', { left: spotsLeft, total: spotsTotal })}
        {closes && ` · ${t('orderStatus.invite.closes', { date: closes })}`}
      </p>
    </section>
  );
}

function OrderStatus() {
  const { t, i18n } = useTranslation();
  const { publicId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const stateLabel = useStateLabel();

  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getOrderStatus(publicId, token)
      .then(setOrder)
      .catch((err) => setError(err.message));
  }, [publicId, token]);

  let content;

  if (error) {
    content = (
      <>
        <h1 className="st-title">{t('orderStatus.cannotOpen')}</h1>
        <p className="st-lede">{t('orderStatus.checkLink')}</p>
      </>
    );
  } else if (!order) {
    content = <p className="st-lede">{t('common.loading')}</p>;
  } else {
    const locale = DATE_LOCALE[i18n.resolvedLanguage] || 'nl-NL';
    const day = (value, options) => new Date(`${value}T12:00:00`).toLocaleDateString(locale, options);
    const shareUrl = `${window.location.origin}${order.share_path}`;
    // Only "ship_split" has anyone else to invite - the other two are a
    // regular order with no share link, deadline, or painters to track.
    const isGroupOrder = order.shipping_option === 'ship_split';
    const isPickup = order.shipping_option === 'pickup';
    const boardStatus = isGroupOrder ? order.board_status : order.everything_status;
    const paid = order.payment_status === 'paid';
    // A custom order can have several boards.
    const boards = order.boards || [];
    const severalBoards = boards.length > 1 || boards[0]?.quantity > 1;

    // The backend closes the share link once the order stops collecting, the
    // deadline passes, or the board fills up (see join_closed_reason). Offering
    // the link past any of those hands someone a page that only says no.
    const deadlinePassed = order.deadline && new Date(`${order.deadline}T23:59:59`) < new Date();
    const canStillJoin =
      isGroupOrder && order.status === 'collecting' && order.unclaimed_canvases > 0 && !deadlinePassed;

    // One sentence for where things stand.
    let headline;
    if (order.status === 'cancelled') headline = t('orderStatus.cancelled');
    else if (!paid) headline = t(`orderStatus.payment.${order.payment_status}`);
    else if (canStillJoin) headline = t('orderStatus.signupsOpen');
    else headline = boardStatus ? stateLabel(boardStatus, isPickup) : t('orderStatus.state.notStarted');

    const ship = order.ship_to;
    // Canvases painters have claimed, and everything else - the unclaimed
    // spots - which go in the box with the board.
    const claimed = order.individual_spots - order.unclaimed_canvases;
    const withBoard = order.num_canvases - claimed;

    content = (
      <>
        <p className="st-kicker">
          {t('orderStatus.kicker')} #{order.order_number}
        </p>
        <h1 className="st-title">{order.event_name}</h1>
        <p className="st-lede">{headline}</p>
        {!paid && order.status !== 'cancelled' && (
          <Link to={`/order/${order.public_id}/pay?token=${encodeURIComponent(token || '')}`} className="of-btn st-pay">
            {t('orderStatus.payNow')}
            <span aria-hidden="true">&rarr;</span>
          </Link>
        )}

        <dl className="st-facts">
          <div>
            <dt>{severalBoards ? t('orderStatus.boards') : t('orderStatus.board')}</dt>
            <dd>
              {severalBoards
                ? order.boards.map((b, i) => (
                    <span key={`${b.cols}x${b.rows}-${i}`} className="st-board-line">
                      {t('orderStatus.boardsValue', { quantity: b.quantity, width: b.width_cm, height: b.height_cm })}
                    </span>
                  ))
                : `${order.board_width_cm} × ${order.board_height_cm} cm`}
              <small>
                {t('orderStatus.canvasCount', { count: order.num_canvases })}
                {order.canvas_size_cm ? ` · ${order.canvas_size_cm} cm` : ''}
              </small>
            </dd>
          </div>

          {order.board_eta && (
            <div>
              <dt>{isPickup ? t('orderStatus.readyFrom') : t('orderStatus.expected')}</dt>
              <dd>{day(order.board_eta, { weekday: 'long', day: 'numeric', month: 'long' })}</dd>
            </div>
          )}

          <div>
            <dt>{isPickup ? t('orderStatus.pickup') : t('orderStatus.shipsTo')}</dt>
            <dd>
              {ship ? (
                <>
                  {ship.name}
                  <small>
                    {ship.street} {ship.house_number}, {ship.postal_code} {ship.city}
                  </small>
                </>
              ) : (
                t('orderStatus.pickupHere')
              )}
            </dd>
          </div>
        </dl>

        {isGroupOrder && (
          <>
            {canStillJoin && (
              <InviteCard
                url={shareUrl}
                spotsLeft={order.unclaimed_canvases}
                spotsTotal={order.individual_spots}
                deadline={order.deadline}
                locale={locale}
              />
            )}

            <section className="st-block">
              <h2 className="st-label">
                {t('orderStatus.painters')}
                {order.participants.length > 0 && <span>{order.participants.length}</span>}
              </h2>
              <div className="st-claimed">
                <p>
                  <strong>
                    {claimed} / {order.individual_spots}
                  </strong>{' '}
                  {t('orderStatus.claimed')}
                </p>
                <div className="st-bar" aria-hidden="true">
                  <span style={{ width: `${(claimed / Math.max(1, order.individual_spots)) * 100}%` }} />
                </div>
              </div>
              {order.participants.length === 0 ? (
                <p className="st-dim">{t('orderStatus.nobody')}</p>
              ) : (
                <ul className="st-painters">
                  {order.participants.map((p) => (
                    <li key={`${p.email}-${p.joined_at}`}>
                      <span className="st-painter-name">
                        {p.first_name} {p.last_name}
                        {p.num_canvases > 1 && <small>&times; {p.num_canvases}</small>}
                      </span>
                      <StateText status={p.canvas_status} isPickup={false} />
                    </li>
                  ))}
                </ul>
              )}
              <p className="st-note">
                {withBoard > 0 ? t('orderStatus.withBoard', { count: withBoard }) : t('orderStatus.boardOnly')}{' '}
                {t('orderStatus.splitNote')}
              </p>
            </section>
          </>
        )}

        <section className="st-block st-total">
          {order.coupon_code && (
            <p>
              <span>{t('common.discount', { code: order.coupon_code })}</span>
              <span>&minus;&euro;{(order.discount_cents / 100).toFixed(2)}</span>
            </p>
          )}
          <p>
            <span>{paid ? t('orderStatus.paid') : t('orderStatus.total')}</span>
            <strong>&euro;{(order.base_amount_cents / 100).toFixed(2)}</strong>
          </p>
        </section>

        <p className="st-foot">{t('orderStatus.somethingWrong')}</p>
      </>
    );
  }

  return (
    <div className="atelier of">
      <OrderHeader home />
      <main className="st">{content}</main>
    </div>
  );
}

export default OrderStatus;
